import type { InventoryItem } from '@app/types/shop-bff/types';
import Box from '@redbubble/design-system/src/Box';
import Field from '@redbubble/design-system/src/Form/Field';
import Skeleton from '@redbubble/design-system/src/Skeleton';
import Stack from '@redbubble/design-system/src/Stack';
import { useMemo } from 'react';
import { LoadingBoundary } from '../LoadingBoundary';
import { compareDisplayOrder } from './configuratorFormUtils';
import type { ColorOption } from './FieldConfigurators/types';
import {
  ConfiguratorOption,
  getColorOptionConfig,
  getTranslatedString,
} from './utils';

export type ColorFieldConfiguratorProps = {
  locale: string;
  option: ConfiguratorOption;
  currentValue: string;
  filterValues: Array<{
    value: ConfiguratorOption['values'][number];
    inventoryItem: InventoryItem;
  }>;
  errors: Record<string, string>;
};

const ColorFieldSkeleton = () => {
  return (
    <Box marginLeft="xs" marginRight="xs" marginBottom="l">
      <Stack spacing="xs">
        <Skeleton height={26} width={130} animated roundedCorners />
        <Skeleton height={44} animated roundedCorners />
      </Stack>
    </Box>
  );
};

export const ColorFieldConfigurator: React.FC<ColorFieldConfiguratorProps> = ({
  locale,
  option,
  currentValue,
  filterValues,
}) => {
  const label = getTranslatedString(option.publicName, option.key, locale);

  const colorOptions = filterValues
    .map(({ value }) => getColorOptionConfig(value, locale))
    .filter((opt): opt is ColorOption => opt !== null);

  const valueSubtitle = colorOptions.find(
    (a) => a.value === currentValue
  )?.defaultText;

  const sortedOptions = useMemo(
    () =>
      [...colorOptions].sort(compareDisplayOrder).map((option) => ({
        ...option,
        label: option.defaultText,
        imageUrl: option.media,
      })),
    [colorOptions]
  );

  return (
    <LoadingBoundary loadingComponent={<ColorFieldSkeleton />}>
      <Field
        label={label}
        fluid
        type="color"
        showTick
        labelSubtitle={valueSubtitle}
        multiple={false}
        name={option.key}
        value={currentValue}
        options={sortedOptions}
      />
    </LoadingBoundary>
  );
};
